import { useEffect, useMemo, useState, useRef } from 'react';
import { socialAPI, clientGroupAPI } from '../lib/api';
import { sortClientsAlpha } from '../lib/sortClients';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { StellarSelect } from './StellarFormControls';
import { Search, Loader2, Image as ImageIcon, Instagram, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * D86 Iter B → D88 Bugfix — Facebook Page Selector + Client Group Assignment.
 *
 * Opens automatically after the OAuth callback (URL `?pending=facebook`).
 * Lists every Page returned from `/me/accounts` with a checkbox + Select all,
 * a search filter (for operators with 60-70 pages), and lets each row be
 * assigned to a Client Group (a workspace-wide "Apply to all" shortcut is
 * provided for the common case).
 *
 * Tokens are NEVER fetched to the client; the backend keeps them and only
 * the page metadata (id, name, picture, linked IG) flows through this UI.
 *
 * D88 fix — the previous version crashed Radix Select with an empty-string
 * `value` for the "No client group" option, causing the dialog to fall back
 * to the ErrorBoundary while looking like an infinite spinner. Now uses the
 * `NONE_CG` sentinel internally and converts it to `null` on submit. Also
 * adds explicit error + empty states (with a Retry button) and a 25s
 * watchdog so the spinner can never run forever.
 */
const NONE_CG = '__none__';
const FETCH_TIMEOUT_MS = 25_000;

export function PageSelectorDialog({ workspaceId, open, onClose, onAttached }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null); // null | string
  const [pending, setPending] = useState({ pages: [], fb_user_name: '', page_count: 0, ig_count: 0 });
  const [clientGroups, setClientGroups] = useState([]);
  const [defaultCg, setDefaultCg] = useState(NONE_CG);
  const [selected, setSelected] = useState({});  // { page_id: true }
  const [perPageCg, setPerPageCg] = useState({}); // { page_id: client_group_id_or_NONE_CG }
  const [filter, setFilter] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    if (!open || !workspaceId) return;
    setLoading(true);
    setError(null);

    // 25s watchdog so the spinner can never spin forever even if the
    // network stalls or an interceptor swallows the response.
    const watchdog = setTimeout(() => {
      if (aliveRef.current) {
        setError('Timed out loading your Facebook Pages. Please try again.');
        setLoading(false);
      }
    }, FETCH_TIMEOUT_MS);

    let pendingErr = null;
    const pendingPromise = socialAPI.pending(workspaceId)
      .then((r) => r?.data ?? null)
      .catch((e) => {
        pendingErr = e?.response?.data?.detail || e?.message || 'Failed to load Pages';
        return null;
      });
    const cgPromise = clientGroupAPI.list(workspaceId)
      .then((r) => (Array.isArray(r?.data) ? r.data : []))
      .catch(() => []);

    Promise.all([pendingPromise, cgPromise])
      .then(([pendingData, cgs]) => {
        if (!aliveRef.current) return;
        clearTimeout(watchdog);
        if (pendingErr) {
          setError(pendingErr);
          setLoading(false);
          return;
        }
        const safe = pendingData && typeof pendingData === 'object' ? pendingData : { pages: [] };
        const safePages = Array.isArray(safe.pages) ? safe.pages : [];
        setPending({
          pages: safePages,
          fb_user_name: safe.fb_user_name || '',
          page_count: typeof safe.page_count === 'number' ? safe.page_count : safePages.length,
          ig_count: typeof safe.ig_count === 'number' ? safe.ig_count : 0,
          pending: !!safe.pending,
        });
        setClientGroups(sortClientsAlpha(cgs || []));
        // Pre-select ALL pages by default — the operator just spent effort
        // authorizing so the most common case is "connect everything" then
        // optionally trim. They can untick with one click.
        const initSel = {};
        safePages.forEach((p) => {
          if (p?.page_id) initSel[p.page_id] = true;
        });
        setSelected(initSel);
        setPerPageCg({});
        setDefaultCg(NONE_CG);
        setFilter('');
        setLoading(false);
      });

    return () => clearTimeout(watchdog);
  }, [open, workspaceId, reloadKey]);

  const filteredPages = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const all = Array.isArray(pending.pages) ? pending.pages : [];
    if (!f) return all;
    return all.filter((p) =>
      ((p?.page_name) || '').toLowerCase().includes(f) ||
      ((p?.ig_username) || '').toLowerCase().includes(f) ||
      ((p?.category) || '').toLowerCase().includes(f),
    );
  }, [pending.pages, filter]);

  const totalPageCount = (Array.isArray(pending.pages) ? pending.pages.length : 0);
  const allFilteredSelected = filteredPages.length > 0 && filteredPages.every((p) => selected[p?.page_id]);
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const igLinkedSelectedCount = (Array.isArray(pending.pages) ? pending.pages : []).filter(
    (p) => p && selected[p.page_id] && p.ig_business_account_id,
  ).length;

  const toggleAllFiltered = () => {
    const next = { ...selected };
    if (allFilteredSelected) {
      filteredPages.forEach((p) => { if (p?.page_id) delete next[p.page_id]; });
    } else {
      filteredPages.forEach((p) => { if (p?.page_id) next[p.page_id] = true; });
    }
    setSelected(next);
  };

  const cgOptions = useMemo(
    () => [{ value: NONE_CG, label: 'No client group' }].concat(
      (clientGroups || []).map((g) => ({ value: g?.id || NONE_CG, label: g?.name || 'Unnamed' })),
    ),
    [clientGroups],
  );

  const cgIdOrNull = (sentinel) => (sentinel && sentinel !== NONE_CG ? sentinel : null);

  const attach = async () => {
    const page_ids = Object.keys(selected).filter((k) => selected[k]);
    if (!page_ids.length) {
      toast.error('Pick at least one Page to connect');
      return;
    }
    setBusy(true);
    try {
      const assignments = page_ids
        .map((pid) => ({ pid, cg: cgIdOrNull(perPageCg[pid]) }))
        .filter(({ cg }) => cg)
        .map(({ pid, cg }) => ({ page_id: pid, client_group_id: cg }));
      const res = await socialAPI.attach(workspaceId, {
        page_ids,
        default_client_group_id: cgIdOrNull(defaultCg),
        assignments,
      });
      const attached = res?.data?.attached ?? page_ids.length;
      const igTotal = res?.data?.ig_total ?? 0;
      toast.success(`Connected ${attached} page(s) · ${igTotal} Instagram link(s) captured`);
      onAttached && onAttached(res?.data);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to connect pages');
    } finally {
      setBusy(false);
    }
  };

  const retry = () => {
    setError(null);
    setLoading(true);
    setReloadKey((k) => k + 1);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col" data-testid="d86-page-selector">
        <DialogHeader>
          <DialogTitle>Select Facebook Pages to connect</DialogTitle>
          <DialogDescription>
            Authorized as <span className="text-foreground font-medium" data-testid="d88-fb-user-name">
              {loading ? '…' : (pending.fb_user_name || 'your Facebook account')}
            </span>.{' '}
            {totalPageCount > 0 ? (
              <>
                Pick which of the <span data-testid="d88-page-total">{totalPageCount}</span>{' '}
                Page{totalPageCount === 1 ? '' : 's'} should be active for posting, and
                (optionally) assign each one to a Client Group.
              </>
            ) : loading ? (
              <>Loading your Pages…</>
            ) : (
              <>No Pages were returned from Facebook.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="d88-loading">
            <Loader2 className="h-6 w-6 animate-spin text-gold/50" />
            <span className="ml-2 text-[12px] text-muted-foreground">Loading Pages from Facebook…</span>
          </div>
        ) : error ? (
          <div
            className="flex flex-col items-center justify-center gap-3 py-10 text-center"
            data-testid="d88-error"
          >
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <div className="text-[12px] text-muted-foreground max-w-md">
              Couldn&apos;t load your Facebook Pages.
              <div className="text-foreground/80 mt-1">{error}</div>
            </div>
            <Button size="sm" onClick={retry} data-testid="d88-retry">Try again</Button>
          </div>
        ) : totalPageCount === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center" data-testid="d88-empty">
            <div className="text-[12px] text-muted-foreground">
              No Pages found on this Facebook account. Make sure you&apos;re using an account that
              manages at least one Facebook Page, then re-authorize.
            </div>
            <Button variant="outline" size="sm" onClick={() => onClose && onClose()}>Close</Button>
          </div>
        ) : (
          <>
            {/* Filters + bulk controls */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60" />
                <Input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter by page name, IG handle, or category…"
                  className="h-8 pl-7 text-[12px]"
                  data-testid="d86-page-filter"
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-[11px]"
                onClick={toggleAllFiltered}
                data-testid="d86-select-all"
              >
                {allFilteredSelected ? 'Deselect all' : 'Select all'}
                <span className="ml-1 text-muted-foreground">({filteredPages.length})</span>
              </Button>
            </div>

            {/* Default Client Group shortcut */}
            <div className="mt-2 flex items-center gap-2 text-[11px]" data-testid="d86-default-cg-row">
              <span className="text-muted-foreground">Assign all to:</span>
              <div className="flex-1 max-w-xs">
                <StellarSelect
                  value={defaultCg}
                  onValueChange={setDefaultCg}
                  options={cgOptions}
                  placeholder="Pick a Client Group (optional)"
                />
              </div>
              <span className="text-muted-foreground/60">
                {selectedCount} selected
                {igLinkedSelectedCount > 0 && ` · ${igLinkedSelectedCount} with Instagram`}
              </span>
            </div>

            {/* Pages list */}
            <div className="mt-3 flex-1 overflow-y-auto border border-border/30 rounded-md divide-y divide-border/20" data-testid="d86-page-list">
              {filteredPages.length === 0 ? (
                <div className="p-6 text-center text-[12px] text-muted-foreground">No pages match your filter.</div>
              ) : (
                filteredPages.map((p) => {
                  const pid = p?.page_id;
                  if (!pid) return null;
                  const cgValue = perPageCg[pid] ?? NONE_CG;
                  return (
                    <label
                      key={pid}
                      className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-foreground/[0.02]"
                      data-testid={`d86-page-row-${pid}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!selected[pid]}
                        onChange={(e) =>
                          setSelected((s) => ({ ...s, [pid]: e.target.checked }))
                        }
                        className="h-4 w-4 accent-gold"
                        data-testid={`d86-page-check-${pid}`}
                      />
                      <div className="h-8 w-8 rounded-full bg-foreground/5 border border-border/40 overflow-hidden shrink-0 flex items-center justify-center">
                        {p?.picture_url ? (
                          <img src={p.picture_url} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <ImageIcon className="h-4 w-4 text-muted-foreground/40" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-medium truncate">{p?.page_name || 'Untitled Page'}</div>
                        <div className="text-[10px] text-muted-foreground flex items-center gap-2">
                          {p?.category && <span>{p.category}</span>}
                          {p?.ig_business_account_id && (
                            <span className="inline-flex items-center gap-0.5 text-pink-400">
                              <Instagram className="h-3 w-3" />
                              {p?.ig_username || 'IG linked'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-44 shrink-0" onClick={(e) => e.preventDefault()}>
                        <StellarSelect
                          value={cgValue}
                          onValueChange={(v) => setPerPageCg((m) => ({ ...m, [pid]: v }))}
                          options={cgOptions}
                          placeholder="Client group"
                        />
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </>
        )}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onClose && onClose()} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={attach}
            disabled={busy || loading || !!error || selectedCount === 0}
            data-testid="d86-connect-selected"
          >
            {busy ? 'Connecting…' : `Connect ${selectedCount || ''} page${selectedCount === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
